// apps/api/src/qxcircle/qxcircle-search.ts
// Description: QXCircle local search — posts and authors (Slice 9).
// Owner: Masa Bayu
// Date: 2026-08-26

import { BadRequestException } from "./_nestjs-common";
import { QXCIRCLE_PROFILE_SEEDS } from "./qxcircle-profile-seeds";
import type { QxCircleFeedPost, QxCircleSearchResponse } from "./qxcircle.types";

export function buildQxCircleSearch(
  queryRaw: string,
  posts: QxCircleFeedPost[],
  hiddenPostIds: Set<string>,
  blockedHandles: Set<string>,
  present: (post: QxCircleFeedPost) => QxCircleFeedPost,
): QxCircleSearchResponse {
  const query = queryRaw.trim();
  if (!query) {
    throw new BadRequestException("Search query required");
  }
  const needle = query.toLowerCase();
  const visible = posts.filter(
    (p) => !hiddenPostIds.has(p.id) && !blockedHandles.has(p.authorHandle),
  );
  const hits = visible
    .filter(
      (p) =>
        p.body.toLowerCase().includes(needle) ||
        p.authorName.toLowerCase().includes(needle) ||
        p.authorHandle.toLowerCase().includes(needle),
    )
    .map((p) => present(p));
  const authorMap = new Map<
    string,
    { authorHandle: string; authorName: string; snippet: string }
  >();
  for (const post of hits) {
    authorMap.set(post.authorHandle, {
      authorHandle: post.authorHandle,
      authorName: post.authorName,
      snippet: post.body.slice(0, 80),
    });
  }
  for (const seed of QXCIRCLE_PROFILE_SEEDS) {
    if (blockedHandles.has(seed.authorHandle)) continue;
    if (
      seed.authorName.toLowerCase().includes(needle) ||
      seed.authorHandle.toLowerCase().includes(needle) ||
      seed.bio.toLowerCase().includes(needle)
    ) {
      authorMap.set(seed.authorHandle, {
        authorHandle: seed.authorHandle,
        authorName: seed.authorName,
        snippet: seed.bio,
      });
    }
  }
  return {
    query,
    authors: [...authorMap.values()],
    posts: hits,
    world: "qxcircle",
    slice: "profile",
  };
}
